/** Nebius foundations. OKLab conversion matrices from Björn Ottosson's reference implementation. */
export const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const
export type Shade = (typeof SHADES)[number]
export const SCALES = ["primary", "secondary", "neutral", "danger", "warning", "success", "support-1", "support-2", "support-3"] as const
export type ScaleName = (typeof SCALES)[number]
export type Scale = { master: string; overrides: Partial<Record<Shade, string>> }
export type Foundations = Record<ScaleName, Scale>
export const DEFAULT_MASTERS: Record<ScaleName, string> = {
  primary: "#0048CA", secondary: "#64748B", neutral: "#737373",
  danger: "#DC2626", warning: "#D97706", success: "#16A34A",
  "support-1": "#8B5CF6", "support-2": "#06B6D4", "support-3": "#EC4899",
}
export function normalizeHex(value: string): string | null {
  const hex = value.trim().replace(/^#/, "")
  if (/^[0-9a-f]{3}$/i.test(hex)) return `#${hex.split("").map(c => c + c).join("")}`.toUpperCase()
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex.toUpperCase()}` : null
}
export function defaultFoundations(): Foundations {
  return Object.fromEntries(SCALES.map(name => [name, { master: DEFAULT_MASTERS[name], overrides: {} }])) as Foundations
}
const linear = (v: number) => v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
const gamma = (v: number) => v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055
export function hexToOklch(hex: string): [number, number, number] {
  const valid = normalizeHex(hex)
  if (!valid) throw new Error("Invalid HEX color")
  const [r, g, b] = [1, 3, 5].map(i => linear(parseInt(valid.slice(i, i + 2), 16) / 255))
  const l = Math.cbrt(0.4122214708*r + 0.5363325363*g + 0.0514459929*b)
  const m = Math.cbrt(0.2119034982*r + 0.6806995451*g + 0.1073969566*b)
  const s = Math.cbrt(0.0883024619*r + 0.2817188376*g + 0.6299787005*b)
  const L = 0.2104542553*l + 0.793617785*m - 0.0040720468*s
  const a = 1.9779984951*l - 2.428592205*m + 0.4505937099*s
  const bb = 0.0259040371*l + 0.7827717662*m - 0.808675766*s
  return [L, Math.sqrt(a*a + bb*bb), Math.atan2(bb, a)]
}
function toLinearRgb(L: number, C: number, h: number) {
  const a = C*Math.cos(h), b = C*Math.sin(h)
  const l = (L + 0.3963377774*a + 0.2158037573*b) ** 3
  const m = (L - 0.1055613458*a - 0.0638541728*b) ** 3
  const s = (L - 0.0894841775*a - 1.291485548*b) ** 3
  return [4.0767416621*l - 3.3077115913*m + 0.2309699292*s, -1.2684380046*l + 2.6097574011*m - 0.3413193965*s, -0.0041960863*l - 0.7034186147*m + 1.707614701*s]
}
export function oklchToHex(L: number, C: number, h: number) {
  let low = 0, high = C
  let rgb = toLinearRgb(L, C, h)
  if (rgb.some(v => v < 0 || v > 1)) {
    for (let i = 0; i < 24; i++) {
      const mid = (low + high) / 2
      const candidate = toLinearRgb(L, mid, h)
      if (candidate.every(v => v >= 0 && v <= 1)) low = mid
      else high = mid
    }
    rgb = toLinearRgb(L, low, h)
  }
  return `#${rgb.map(v => Math.round(Math.min(1, Math.max(0, gamma(v))) * 255).toString(16).padStart(2, "0")).join("")}`.toUpperCase()
}
export function generateScale(scale: Scale): Record<Shade, string> {
  const [L, C, h] = hexToOklch(scale.master)
  const lightT = [0.96, 0.88, 0.70, 0.48, 0.24]
  const darkT = [0.16, 0.32, 0.49, 0.66, 0.82]
  return Object.fromEntries(SHADES.map((shade, i) => {
    if (scale.overrides[shade]) return [shade, scale.overrides[shade]]
    if (shade === 500) return [shade, normalizeHex(scale.master)!]
    const t = i < 5 ? lightT[i] : darkT[i - 6]
    const lightness = i < 5 ? L + (0.99-L)*t : L*(1-t)
    const chroma = C * (i < 5 ? 1 - t*0.93 : 1-t*0.65)
    return [shade, oklchToHex(lightness, chroma, h)]
  })) as Record<Shade, string>
}
export function luminance(hex: string) {
  const normalized = normalizeHex(hex)
  if (!normalized) throw new Error("Invalid HEX color")
  const [r,g,b] = [1,3,5].map(i => linear(parseInt(normalized.slice(i,i+2),16)/255))
  return 0.2126*r+0.7152*g+0.0722*b
}
export function contrast(a: string, b: string) {
  const x = luminance(a), y = luminance(b)
  return (Math.max(x,y)+0.05)/(Math.min(x,y)+0.05)
}
export function foregroundFor(background: string) {
  return contrast(background,"#FFFFFF") >= contrast(background,"#000000") ? "#FFFFFF" : "#000000"
}
